import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  FiChevronLeft,
  FiShield,
  FiUserCheck,
  FiDatabase,
  FiShare2,
  FiLock,
  FiKey,
  FiMail
} from 'react-icons/fi';

const PrivacyPolicyPage = () => {
  const [activeSection, setActiveSection] = useState('');
  const containerRef = useRef(null);

  const sectionRefs = {
    section1: useRef(null),
    section2: useRef(null),
    section3: useRef(null),
    section4: useRef(null),
    section5: useRef(null),
    section6: useRef(null),
    section7: useRef(null)
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.getAttribute('id'));
          }
        });
      },
      { rootMargin: '-20% 0px -75% 0px' }
    );

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      Object.values(sectionRefs).forEach((ref) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) section.scrollIntoView({ behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } }
  };

  const tableOfContents = [
    { id: 'section-1', title: 'Introducción', icon: <FiShield /> },
    { id: 'section-2', title: 'Datos que recopilamos', icon: <FiDatabase /> },
    { id: 'section-3', title: 'Finalidad del tratamiento', icon: <FiUserCheck /> },
    { id: 'section-4', title: 'Con quién compartimos datos', icon: <FiShare2 /> },
    { id: 'section-5', title: 'Conservación y seguridad', icon: <FiLock /> },
    { id: 'section-6', title: 'Tus derechos', icon: <FiKey /> },
    { id: 'section-7', title: 'Contacto', icon: <FiMail /> }
  ];

  return (
    <>
      <Helmet>
        <title>Política de Privacidad | Avlink Network</title>
        <meta
          name="description"
          content="Política de privacidad de Avlink Network: qué datos personales recopilamos, cómo los utilizamos, con quién los compartimos y cómo podés ejercer tus derechos."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.avlinknetwork.org/legal/privacy" />
      </Helmet>

      <div className="pt-24 pb-20 bg-dark-900" ref={containerRef}>
        {/* Hero */}
        <section className="relative overflow-hidden py-20 mb-16">
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage:
                'radial-gradient(rgba(255, 255, 255, 0.8) 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}
          />
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Política de <span className="text-primary-500">Privacidad</span>
              </h1>
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                En Avlink Network respetamos tu privacidad. Acá te explicamos qué datos
                personales tratamos, con qué finalidad y cómo podés ejercer tus derechos.
              </p>
              <div className="flex items-center justify-center text-sm text-gray-400">
                <FiShield className="mr-2 text-primary-500" />
                <span>Última actualización: 11 de mayo de 2026</span>
              </div>
            </motion.div>
          </div>
        </section>

        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Sidebar TOC */}
            <aside className="md:w-1/4 lg:w-1/5">
              <div className="sticky top-24">
                <div className="bg-dark-800/80 backdrop-blur-sm rounded-xl border border-dark-600 p-5 mb-6">
                  <h3 className="text-lg font-bold text-white mb-4">Contenido</h3>
                  <nav>
                    <ul className="space-y-3">
                      {tableOfContents.map((item) => (
                        <li key={item.id}>
                          <button
                            onClick={() => scrollTo(item.id)}
                            className={`w-full flex items-center p-2 rounded-lg text-left transition-colors ${
                              activeSection === item.id
                                ? 'bg-primary-900/30 text-primary-400 border-l-2 border-primary-500 pl-3'
                                : 'text-gray-400 hover:text-white hover:bg-dark-700'
                            }`}
                          >
                            <span className="mr-3 text-primary-500">{item.icon}</span>
                            <span className="text-sm">{item.title}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>

                <Link
                  to="/"
                  className="w-full flex items-center justify-center py-3 px-4 bg-dark-800 hover:bg-dark-700 border border-dark-600 rounded-lg text-gray-300 hover:text-white transition-colors text-sm"
                >
                  <FiChevronLeft className="mr-2" />
                  Volver al inicio
                </Link>
              </div>
            </aside>

            {/* Contenido */}
            <div className="md:w-3/4 lg:w-4/5">
              <div className="bg-dark-800/80 backdrop-blur-sm rounded-xl border border-dark-600 p-6 lg:p-8 mb-12">
                <div className="bg-dark-700/80 border-l-4 border-primary-500 p-4 mb-8">
                  <p className="font-medium text-white">RESUMEN:</p>
                  <p className="text-gray-300">
                    Tratamos tus datos personales únicamente para prestar el servicio de la
                    plataforma. No los vendemos a terceros. Podés ejercer tus derechos de
                    acceso, rectificación, supresión y oposición en cualquier momento
                    escribiendo a contacto@avlink-network.com.
                  </p>
                </div>

                {/* 1. Introducción */}
                <motion.section
                  id="section-1"
                  ref={sectionRefs.section1}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      1
                    </span>
                    <span>INTRODUCCIÓN</span>
                  </h2>
                  <div className="space-y-4 text-gray-300">
                    <p>
                      Avlink Network (en adelante, "nosotros", "la plataforma" o "Avlink") es
                      el responsable del tratamiento de los datos personales recogidos a través
                      del sitio{' '}
                      <a
                        href="https://www.avlinknetwork.org"
                        className="text-primary-400 hover:underline"
                      >
                        www.avlinknetwork.org
                      </a>{' '}
                      y de los servicios asociados.
                    </p>
                    <p>
                      Esta política aplica a todos los usuarios del sitio: profesionales
                      individuales, empresas, sponsors y visitantes. Al utilizar la
                      plataforma aceptás el tratamiento de tus datos personales en los términos
                      descritos a continuación.
                    </p>
                  </div>
                </motion.section>

                {/* 2. Datos que recopilamos */}
                <motion.section
                  id="section-2"
                  ref={sectionRefs.section2}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      2
                    </span>
                    <span>DATOS QUE RECOPILAMOS</span>
                  </h2>
                  <p className="mb-4 text-gray-300">
                    Recopilamos únicamente los datos que necesitamos para prestar el servicio:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2">Datos de cuenta</h3>
                      <p className="text-gray-300 text-sm">
                        Nombre, correo electrónico, contraseña cifrada, tipo de cuenta y rol.
                      </p>
                    </div>
                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2">Datos de perfil</h3>
                      <p className="text-gray-300 text-sm">
                        Foto, biografía, ciudad, país, especialidad, experiencia laboral,
                        certificaciones, redes sociales y links de portfolio.
                      </p>
                    </div>
                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2">Datos de empresa</h3>
                      <p className="text-gray-300 text-sm">
                        Razón social, logo, descripción, ubicación, tipo de empresa, catálogo
                        y datos de contacto comercial.
                      </p>
                    </div>
                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2">Datos de uso</h3>
                      <p className="text-gray-300 text-sm">
                        Páginas visitadas, eventos en los que participás, postulaciones a
                        empleos, mensajes y métricas agregadas de comportamiento.
                      </p>
                    </div>
                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600 md:col-span-2">
                      <h3 className="font-medium text-white mb-2">Datos técnicos</h3>
                      <p className="text-gray-300 text-sm">
                        Dirección IP, tipo de dispositivo, navegador, sistema operativo,
                        identificadores de sesión y registros de actividad necesarios para
                        seguridad y diagnóstico.
                      </p>
                    </div>
                  </div>
                </motion.section>

                {/* 3. Finalidad */}
                <motion.section
                  id="section-3"
                  ref={sectionRefs.section3}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      3
                    </span>
                    <span>FINALIDAD DEL TRATAMIENTO</span>
                  </h2>
                  <ul className="list-disc pl-5 space-y-2 text-gray-300">
                    <li>Prestar y mantener los servicios de la plataforma.</li>
                    <li>
                      Autenticar tu identidad y proteger tu cuenta frente a accesos no
                      autorizados.
                    </li>
                    <li>
                      Conectar a profesionales con empresas que publican ofertas de empleo y
                      búsquedas en el sector AV.
                    </li>
                    <li>
                      Enviar comunicaciones operativas (recuperación de contraseña, avisos de
                      seguridad, cambios en los términos) y newsletters si te suscribiste.
                    </li>
                    <li>
                      Mostrar tu perfil públicamente sólo si activaste los toggles de
                      privacidad correspondientes (perfil de profesional o página de empresa).
                    </li>
                    <li>
                      Cumplir con obligaciones legales aplicables y resolver disputas o
                      reclamaciones.
                    </li>
                    <li>
                      Mejorar la plataforma a partir de métricas agregadas y anónimas de uso.
                    </li>
                  </ul>
                </motion.section>

                {/* 4. Con quién compartimos */}
                <motion.section
                  id="section-4"
                  ref={sectionRefs.section4}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      4
                    </span>
                    <span>CON QUIÉN COMPARTIMOS DATOS</span>
                  </h2>
                  <p className="mb-4 text-gray-300">
                    No vendemos datos personales. Sólo los compartimos en los siguientes casos:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-300">
                    <li>
                      <strong className="text-white">Proveedores de infraestructura</strong> —
                      Supabase (base de datos y autenticación) y Vercel (hosting y entrega de
                      contenido). Actúan como encargados del tratamiento bajo nuestras
                      instrucciones.
                    </li>
                    <li>
                      <strong className="text-white">Empresas reclutadoras</strong> — cuando
                      aplicás a una oferta laboral, la empresa que publicó la búsqueda accede a
                      los datos que decidiste compartir en tu postulación.
                    </li>
                    <li>
                      <strong className="text-white">Otros usuarios</strong> — la información
                      que publicás voluntariamente en tu perfil público es visible para otros
                      usuarios del sitio y, si activaste el toggle correspondiente, para
                      buscadores como Google.
                    </li>
                    <li>
                      <strong className="text-white">Autoridades</strong> — cuando exista una
                      obligación legal de cooperación con autoridades públicas competentes.
                    </li>
                  </ul>
                </motion.section>

                {/* 5. Conservación y seguridad */}
                <motion.section
                  id="section-5"
                  ref={sectionRefs.section5}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      5
                    </span>
                    <span>CONSERVACIÓN Y SEGURIDAD</span>
                  </h2>
                  <div className="space-y-4 text-gray-300">
                    <p>
                      Conservamos tus datos personales mientras tu cuenta esté activa o sea
                      necesario para los fines descritos. Una vez eliminada tu cuenta,
                      mantenemos sólo los datos imprescindibles para cumplir obligaciones
                      legales (por ejemplo, registros fiscales o de seguridad), durante el
                      plazo legalmente exigido.
                    </p>
                    <p>
                      Aplicamos medidas técnicas y organizativas razonables para proteger tus
                      datos frente a accesos no autorizados, alteración, pérdida o
                      destrucción: cifrado en tránsito (HTTPS), contraseñas hasheadas, control
                      de acceso por roles, copias de seguridad y registros de auditoría.
                    </p>
                  </div>
                </motion.section>

                {/* 6. Derechos */}
                <motion.section
                  id="section-6"
                  ref={sectionRefs.section6}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      6
                    </span>
                    <span>TUS DERECHOS</span>
                  </h2>
                  <p className="mb-4 text-gray-300">
                    Como titular de los datos personales podés ejercer en cualquier momento
                    los siguientes derechos:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-300">
                    <li>
                      <strong className="text-white">Acceso</strong> — solicitar copia de los
                      datos que tratamos sobre vos.
                    </li>
                    <li>
                      <strong className="text-white">Rectificación</strong> — corregir datos
                      inexactos o desactualizados (también podés hacerlo desde la edición de
                      tu perfil).
                    </li>
                    <li>
                      <strong className="text-white">Supresión</strong> — solicitar la
                      eliminación de tu cuenta y datos asociados.
                    </li>
                    <li>
                      <strong className="text-white">Oposición</strong> — oponerte a un
                      tratamiento específico, por ejemplo dejar de recibir comunicaciones.
                    </li>
                    <li>
                      <strong className="text-white">Portabilidad</strong> — recibir tus datos
                      en un formato estructurado y de uso común.
                    </li>
                    <li>
                      <strong className="text-white">Revocación</strong> — retirar tu
                      consentimiento cuando éste sea la base legal del tratamiento.
                    </li>
                  </ul>
                  <p className="mt-4 text-gray-300">
                    Para ejercer cualquiera de estos derechos, escribinos a{' '}
                    <a
                      href="mailto:contacto@avlink-network.com"
                      className="text-primary-400 hover:underline"
                    >
                      contacto@avlink-network.com
                    </a>{' '}
                    indicando tu identidad y la solicitud concreta.
                  </p>
                </motion.section>

                {/* 7. Contacto */}
                <motion.section
                  id="section-7"
                  ref={sectionRefs.section7}
                  className="mb-4"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      7
                    </span>
                    <span>CONTACTO</span>
                  </h2>
                  <p className="text-gray-300">
                    Si tenés consultas sobre esta política o sobre el tratamiento de tus
                    datos, podés escribir a{' '}
                    <a
                      href="mailto:contacto@avlink-network.com"
                      className="text-primary-400 hover:underline"
                    >
                      contacto@avlink-network.com
                    </a>{' '}
                    o usar el{' '}
                    <Link to="/contact" className="text-primary-400 hover:underline">
                      formulario de contacto
                    </Link>{' '}
                    del sitio.
                  </p>
                  <p className="mt-4 text-gray-300">
                    Esta política puede ser actualizada para reflejar cambios en la
                    plataforma o en la normativa aplicable. Te notificaremos los cambios
                    relevantes a través del sitio o por correo electrónico.
                  </p>
                </motion.section>
              </div>

              <div className="text-center text-gray-500 text-sm">
                © {new Date().getFullYear()} Avlink Network. Todos los derechos reservados.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
